import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useEffect } from 'react'

import { axiosInstance } from '../../../helpers'
import { PaginatedResponse, ResidentTerm, TimePeriod } from '../types'

export interface UseResidentTermsOptions {
    auditId: number
}

export interface UpdateTimePeriodParams {
    residentTermId: number
    timePeriod: TimePeriod
}

export interface SetTimePeriodDatesParams {
    pastDate: string
    currentDate: string
    futureDate: string
}

export interface FetchResidentTermDocumentsParams {
    residentTermId: number
}

export const useResidentTerms = (options: UseResidentTermsOptions) => {
    const { auditId } = options
    const queryClient = useQueryClient()

    // Fetch resident terms
    const {
        data: residentTermsData,
        isLoading,
        isFetching,
        dataUpdatedAt,
        error,
        refetch,
    } = useQuery<AxiosResponse<PaginatedResponse<ResidentTerm>>, Error>({
        queryKey: ['residentTerms', auditId],
        queryFn: async () => {
            return axiosInstance.get(`/lease-audit/${auditId}/resident-terms/`)
        },
        enabled: !!auditId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Update time period for a resident term
    const updateTimePeriodMutation = useMutation({
        mutationFn: (params: UpdateTimePeriodParams) =>
            axiosInstance.patch(
                `/lease-audit/${auditId}/resident-terms/${params.residentTermId}/`,
                { term_status: params.timePeriod.toUpperCase() },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['residentTerms', auditId],
            })
        },
    })

    // Set time period dates (for auto-categorization)
    const setTimePeriodDatesMutation = useMutation({
        mutationFn: (params: SetTimePeriodDatesParams) =>
            axiosInstance.post(
                `/lease-audit/${auditId}/resident-terms/set-time-periods/`,
                params,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['residentTerms', auditId],
            })
        },
    })

    // Fetch documents for a specific resident term
    const fetchResidentTermDocumentsMutation = useMutation({
        mutationFn: async (params: FetchResidentTermDocumentsParams) => {
            return axiosInstance.get(
                `/lease-audit/${auditId}/resident-terms/documents/?resident_term_id=${params.residentTermId}`,
            )
        },
        onSuccess: (response, variables) => {
            // Update the specific resident term in the cache with the fetched documents
            queryClient.setQueryData(
                ['residentTerms', auditId],
                (oldData: any) => {
                    if (!oldData) return oldData

                    const isArray = Array.isArray(oldData.data)
                    if (isArray) {
                        // Handle array response
                        const updatedArray = oldData.data.map(
                            (term: ResidentTerm) =>
                                term.id === variables.residentTermId
                                    ? {
                                          ...term,
                                          paired_documents: response.data,
                                      }
                                    : term,
                        )
                        return { ...oldData, data: updatedArray }
                    } else {
                        // Handle paginated response
                        const updatedResults = oldData.data.results.map(
                            (term: ResidentTerm) =>
                                term.id === variables.residentTermId
                                    ? {
                                          ...term,
                                          paired_documents: response.data,
                                      }
                                    : term,
                        )
                        return {
                            ...oldData,
                            data: { ...oldData.data, results: updatedResults },
                        }
                    }
                },
            )
        },
    })

    // Export missing documents - generates a report
    const exportMissingMutation = useMutation({
        mutationFn: (residentTermIds: number[]) =>
            axiosInstance.post(
                `/lease-audit/${auditId}/resident-terms/export-missing-resident-terms/`,
                { resident_term_ids: residentTermIds },
            ),
        onSuccess: () => {
            // Invalidate reports query so new report appears
            queryClient.invalidateQueries({
                queryKey: ['leaseAuditReports', auditId],
            })
        },
    })

    const response = residentTermsData?.data
    // Handle both array response and paginated response
    const residentTerms = Array.isArray(response)
        ? response
        : response?.results ?? []
    const totalCount = Array.isArray(response)
        ? response.length
        : response?.count ?? 0

    return {
        // Data
        residentTerms,
        totalCount,

        // Loading states
        isLoading,
        isUpdatingTimePeriod: updateTimePeriodMutation.isLoading,
        isSettingTimePeriodDates: setTimePeriodDatesMutation.isLoading,
        isFetchingDocuments: fetchResidentTermDocumentsMutation.isLoading,
        isExportingMissing: exportMissingMutation.isLoading,

        // Error states
        error,

        // Actions
        refetch,
        updateTimePeriod: updateTimePeriodMutation.mutate,
        setTimePeriodDates: setTimePeriodDatesMutation.mutate,
        fetchResidentTermDocuments: fetchResidentTermDocumentsMutation.mutate,
        exportMissing: exportMissingMutation.mutate,
    }
}
